import { Drawer, Modal, message } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DrawerHeader, ModalHeader, SearchFilters } from 'datastake-daf/dist/components';
import Create from './Create';
import EditPartner from './Edit';
import DafTable from '../../../../../../components/Table/newTable';
import { CREATE_DRAWER_WIDTH } from '../../../../../../helpers/form';
import { useFilters } from '../../../../../../helpers/SubjectSearch';
import PartnerService from '../../../../../../services/PartnerService';
import TablePagination from '../../../../../../components/Table/pagination';
import { checkboxConfig, getColumns, selectFiltersConfig, filtersConfig } from '../config';

const module = 'tazama'

export default function Base({
  loading,
  activeTab,
  createVisible,
  setLoading = () => { },
  setTotalRequests = () => { },
  requestDataFetch = () => { },
  setCreateVisible = () => { },
  setRequestDataFetch = () => { },
}) {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.authentication);
  const [showFilters, setShowFilters] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [data, setData] = useState([]);
  const [initFetchDone, setInitFetchDone] = useState(false);
  const [selectOptions, setSelectOptions] = useState({ category: [] });
  const [editVisible, setEditVisible] = useState(false);

  const {
    newFiltersConfig,
    pagination,
    activeFilters,
    onSearch,
    defaultFilters,
    onTableChange,
    setPagination,
    onFiltersChange,
    setActiveFilters,
    totalPages,
    canGoNext,
    canGoPrev,
    goPrev,
    goNext,
  } = useFilters({
    selectFiltersConfig: selectFiltersConfig,
    view: 'partners',
    filtersConfig,
    module,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await PartnerService.get(activeTab, { ...activeFilters, type: activeTab });
      setData(data.data);
      setSelectOptions({ category: data?.meta?.categoriesOptions || [] });
      setPagination((prev) => ({ ...prev, total: data?.meta?.total || 0 }));
      setTotalRequests(data?.meta?.total || 0);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);

    if (!initFetchDone) {
      setInitFetchDone(true);
    }
  }, [activeTab, activeFilters, initFetchDone, setLoading, setPagination]);

  const accept = useCallback(async (id) => {
    setLoading(true);

    try {
      await PartnerService.accept(id);
      setRequestDataFetch(true);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [setLoading]);

  const decline = useCallback(async (id) => {
    setLoading(true);

    try {
      await PartnerService.decline(id);
      setRequestDataFetch(true);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [setLoading]);

  const activate = useCallback(async (id) => {
    setLoading(true);

    try {
      await PartnerService.activate(id);
      setRequestDataFetch(true);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [setLoading]);

  const suspend = useCallback(async (id) => {
    setLoading(true);

    try {
      await PartnerService.suspend(id);
      message.success(t('Partner suspended successfully'))
      setRequestDataFetch(true);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [setLoading]);

  const resendInvite = useCallback(async (id) => {
    setLoading(true);

    try {
      await PartnerService.resendInvite(id);
      await fetchData();
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [fetchData, setLoading]);

  const block = useCallback(async (id) => {
    setLoading(true);

    try {
      await PartnerService.block(id);
      await fetchData();
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [fetchData, setLoading]);

  const columns = useMemo(() => getColumns({
    t,
    accept,
    decline,
    resendInvite,
    suspend,
    block,
    activate,
    activeTab,
    setEditVisible,
    selectOptions,
  }), [t, activeTab, accept, decline, suspend, activate, selectOptions, block, resendInvite]);

  useEffect(() => {
    if (requestDataFetch) {
      fetchData();
      setRequestDataFetch(false);
    }
  }, [requestDataFetch]);

  useEffect(() => {
    fetchData();
  }, [activeFilters, user?.language]);

  useEffect(() => {
    if (initFetchDone) {
      setActiveFilters({ ...activeFilters, page: 1, tab: activeTab });
      setPagination({ ...pagination, current: 1 });
    }
  }, [activeTab]);

  const canClearSearch = useMemo(() => {
    return !!activeFilters.search;
  }, [activeFilters]);

  return (
    <>
      <>
        <div className='mt-6 ml-6 mr-6'>
          <SearchFilters
            t={t}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            hasError={hasError}
            setHasError={setHasError}
            filtersConfig={newFiltersConfig}
            checkboxConfig={checkboxConfig}
            onSearch={onSearch}
            activeFilters={activeFilters}
            canClear={canClearSearch}
            clearFilters={() => setActiveFilters({})}
          />
        </div>
        <div className='daf-table-wrapper pagination-w-padding'>
          <DafTable
            columns={columns}
            data={data}
            loading={loading}
            hideOnLoading={false}
            pagination={pagination}
            selectOptions={selectOptions}
            sourcesKey="authorId"
            filtersConfig={selectFiltersConfig}
            onFilterChange={onFiltersChange}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            defaultFilters={defaultFilters}
            onChange={onTableChange}
            doEmptyRows
          />
          <TablePagination
            t={t}
            page={pagination.current}
            totalPages={totalPages}
            goPrev={goPrev}
            goNext={goNext}
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            totalItems={pagination.total}
            doTotalItems
            onChangePagination={(val) => {
              onTableChange({ ...pagination, current: 1, pageSize: val });
            }}
            perPage={pagination.pageSize}
            arrowIcons
          />
        </div>
      </>

      <Modal
        open={!!editVisible}
        onCancel={() => setEditVisible(false)}
        footer={null}
        title={<ModalHeader title={t('Edit Settings')} />}
      >
        {editVisible ? (
          <EditPartner
            partner={editVisible}
            fetchData={() => setRequestDataFetch(true)}
            onClose={() => setEditVisible(false)}
          />
        ) : null}
      </Modal>

      {createVisible ? (
        <Drawer
          destroyOnClose={true}
          title={<DrawerHeader title={t("Add Partner")} />}
          tooltip={{
            content: (
              <div className="max-w-250">
                {t("Source: This partner will share information with you.")},
                <br />
                {t("Client: You will share information with this partner.")},
                <br />
                {t("Exchange: You will both share information with each other.")}
              </div>
            ),
          }}
          open={createVisible}
          maskClosable={false}
          onClose={() => {
            setCreateVisible(false);
          }}
          width={CREATE_DRAWER_WIDTH}
          bodyStyle={{ padding: 0 }}
        >
          {createVisible ? (
            <Create
              fetchData={() => setRequestDataFetch(true)}
              onClose={() => setCreateVisible(false)}
            />
          ) : null}
        </Drawer>
      ) : null}

      <input id="myInput" hidden />
    </>
  )
}
